import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Form } from '../../../../bootstrap';

import s from './ControlledRadio.module.scss';

type Props<T extends FieldValues> = {
  fieldName: FieldPathByValue<T, string | undefined>;
  control: Control<T>;
  options: { value: string; label: ReactNode }[];
  label?: ReactNode;
  required?: boolean;
  hideErrorMessage?: boolean;
  size?: 'sm' | 'md';

  optionIdPrefix?: string;
};

export function ControlledRadio<T extends FieldValues>({
  fieldName,
  control,
  options,
  label,
  required,
  hideErrorMessage,
  size = 'md',
  optionIdPrefix,
}: Props<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ name: fieldName, control });

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1">*</span>}
        </Form.Label>
      )}
      <div>
        {options.map((option) => (
          <Form.Check
            key={option.value.toString()}
            id={optionIdPrefix != null ? `${optionIdPrefix}-${option.value}` : option.value.toString()}
            type="radio"
            className={classNames({
              'u-fs-2 pb-1': size === 'sm',
              'pb-2': size === 'md',
            })}
          >
            <Form.Check.Input
              type="radio"
              {...field}
              value={option.value}
              checked={field.value?.toString() === option.value} // explicit set so that parent reset() works
              className={s.radio}
            />
            <Form.Check.Label className="u-pointer">{option.label}</Form.Check.Label>
          </Form.Check>
        ))}
      </div>
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any} // This *should* work without casting but it doesn't
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
